import { makeObservable, computed, observable, action } from 'mobx';
import { RawDraftContentState } from 'draft-js';

import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { JobModel, User } from '../../entities';
import { CommentThreadState } from './commentThreadState';
import { CommentInputState } from './commentInputState';
import { Comment } from '../../entities/comment';
import { CommentDeleteWindowState } from './commentDeleteWindowState';

type Props = {
  comment: Comment,
  thread?: CommentThreadState
};

const CONTENT_LINE_HEIGHT = 18;
const CONTENT_OFFSET = 8;

// TODO: add permission guards to toggleEditInput and openDeleteWindow
//       (user must own the comment or have the respective EditComment / DeleteComment permission)
export class CommentState
  extends StoreNode {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      comment: computed,
      commentId: computed,
      videoTime: computed,
      text: computed,
      jobId: computed,
      job: computed,
      userId: computed,
      user: computed,
      parentId: computed,
      thread: computed,
      videoTimeLabel: computed,
      isReply: computed,
      content: computed,
      ownerName: computed,
      isCurrent: computed,
      isLoading: computed,
      isActive: computed,
      isHovered: observable,
      hasContentExpanded: observable,
      hasMenuOpened: observable,
      showReplyInput: observable,
      showEditInput: observable,
      attachDOMContent: action,
      detachDOMContent: action,
      toggleContentExpanded: action,
      handleMenuOpened: action,
      toggleReplyInput: action,
      toggleEditInput: action,
      openDeleteWindow: action,
      submitEdit: action,
      handlePointerEnter: action,
      handlePointerLeave: action,
    });

    if (!this.isReply) {
      this.replyInput = new CommentInputState(this.store, {
        jobId: this.jobId,
        comment: this,
        isReply: true
      });
    }
  }

  readonly replyInput: CommentInputState | null = null;

  readonly editInput: CommentInputState = new CommentInputState(this.store, {
    jobId: this.jobId,
    comment: this,
    isEdit: true
  });

  domContent: HTMLElement | null = null;

  get comment(): Comment {
    return this.resolvedProps.comment;
  }

  get commentId(): string {
    return this.comment.id;
  }

  get videoTime(): number | null {
    return this.comment.videoTime;
  }

  get text(): string | null {
    return this.comment.text;
  }

  get jobId(): string | null {
    return this.comment.jobId;
  }
  get job(): JobModel | null {
    return this.comment.job;
  }

  get userId(): string | null {
    return this.comment.userId;
  }
  get user(): User | null {
    return this.comment.actualUser;
  }

  get parentId(): string | null {
    return this.comment.parentId;
  }

  get thread(): CommentThreadState {
    return this.resolvedProps.thread;
  }

  get videoTimeLabel(): string {
    return this.comment.videoTimeLabel;
  }

  get isReply(): boolean {
    return this.comment.isReply;
  }

  get content(): RawDraftContentState | null {
    return this.comment.content;
  }

  get ownerName(): string | null {
    return (
      this.user?.name ||
      this.user?.username ||
      this.user?.email ||
      this.user?.id ||
      null);
  }

  get isCurrent(): boolean {
    return this.videoTime === this.store.userPlayerPage.player.currentTime;
  }

  get isLoading(): boolean {
    return this.replyInput?.isLoading || this.editInput?.isLoading;
  }

  get isActive(): boolean {
    return (
      this.thread?.isActive ||
      this.showReplyInput ||
      this.showEditInput ||
      this.hasMenuOpened
    );
  }

  isHovered: boolean = false;

  hasContentExpanded: boolean = true;

  hasMenuOpened: boolean = false;

  showReplyInput: boolean = false;

  showEditInput: boolean = false;

  attachDOMContent(content: HTMLElement) {
    this.domContent = content;

    if (this.domContent.clientHeight > (CONTENT_LINE_HEIGHT * 2 + CONTENT_OFFSET)) {
      this.hasContentExpanded = false;
    }
  }

  detachDOMContent() {
    this.domContent = null;
  }

  toggleContentExpanded() {
    this.hasContentExpanded = !this.hasContentExpanded
  }

  handleMenuOpened(isOpened: boolean) {
    this.hasMenuOpened = isOpened;
  }

  toggleReplyInput() {
    if (!this.replyInput) return;
    this.showReplyInput = !this.showReplyInput;

    if (!this.showReplyInput) this.replyInput.clear();

    this.thread?.setHasReplyInput(this.showReplyInput);

    if (this.showReplyInput) {
      this.thread.playerComments?.player?.invoke('pause');
      this.thread.playerComments?.scrollOnReply(this.comment);
    }
  }

  toggleEditInput() {
    this.showEditInput = !this.showEditInput;
    this.editInput.setEditorStateContent();

    if (this.showEditInput) {
      this.thread.playerComments?.player?.invoke('pause');
      this.thread.playerComments?.scrollOnEdit(this.comment);
    }
  }

  openDeleteWindow() {
    const player = this.thread?.playerComments?.player;
    // this.store.commentDeleteWindow.open({ comment: this });

    const window = this.store.commentDeleteWindow;
    const listener = (msg: Message<CommentDeleteWindowState>) => {
      switch (msg.type) {
        case 'open':
          player?.invoke('enterPause');
          break;

        case 'close':
          window.unlisten(listener);
          // player?.invoke('exitPause');
          break;
      }
    }

    window.listen(listener);
    // TODO: pass the comment entity (not the state object) to commentDeleteWindow.open()
    window.open({ comment: this });
  }

  async submitEdit() {
    try {
      const [, err] = await this.editInput.submit();
      if (!err)
        this.toggleEditInput();
    } catch (_error) {
      // TODO: show error notification and restore edit state on failure
    }
  }

  handlePointerEnter(evt: PointerEvent) {
    if (!this.thread) return;
    this.thread.handlePointerEnter(evt);
  }

  handlePointerLeave(evt: PointerEvent) {
    if (!this.thread) return;
    this.thread.handlePointerLeave(evt);
  }
}